import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TemplateCard from './TemplateCard';
import './PinnedTemplates.css';

/**
 * Sortable Template Item
 * Wrapper for TemplateCard with drag-and-drop functionality
 */
const SortableTemplateItem = ({ template, onUnpin, onExecute }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`draggable-item ${isDragging ? 'dragging' : ''}`}>
      <TemplateCard
        template={template}
        onUnpin={onUnpin}
        onExecute={onExecute}
        showDragHandle={true}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

/**
 * Pinned Templates Component
 * Display and manage user's favourite templates with drag-and-drop reordering
 */
const PinnedTemplates = ({ brandId, expertId, onExecute }) => {
  const [pinnedTemplates, setPinnedTemplates] = useState([]);
  const [maxPins, setMaxPins] = useState(8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPinnedTemplates();
  }, [brandId, expertId]);

  const fetchPinnedTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[PinnedTemplates] 📋 Fetching pinned templates');

      const response = await fetch(
        `/api/chat/templates/pinned/${brandId}/${expertId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch pinned templates');

      const data = await response.json();
      setPinnedTemplates(data.pinned || []);
      setMaxPins(data.max || 8);

      console.log('[PinnedTemplates] ✅ Loaded pinned templates:', data.pinned?.length);

    } catch (err) {
      console.error('[PinnedTemplates] ❌ Error fetching pinned templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpin = async (templateId) => {
    console.log('[PinnedTemplates] 🗑️ Unpinning template:', templateId);

    try {
      const response = await fetch(`/api/chat/templates/pin/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ brandId, expertId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to unpin template');
      }

      console.log('[PinnedTemplates] ✅ Unpin successful');

      // Refresh list
      fetchPinnedTemplates();

    } catch (err) {
      console.error('[PinnedTemplates] ❌ Unpin failed:', err);
      alert('Failed to unpin template');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return; // No change
    }

    const oldIndex = pinnedTemplates.findIndex(t => t.id === active.id);
    const newIndex = pinnedTemplates.findIndex(t => t.id === over.id);

    console.log('[PinnedTemplates] 🔄 Reordering:', { from: oldIndex, to: newIndex });

    // Optimistic update
    const reordered = arrayMove(pinnedTemplates, oldIndex, newIndex);
    setPinnedTemplates(reordered);

    // Send to server
    try {
      const templateIds = reordered.map(t => t.id);

      const response = await fetch('/api/chat/templates/pin/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          templateIds,
          brandId,
          expertId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to reorder templates');
      }

      console.log('[PinnedTemplates] ✅ Reorder successful');

    } catch (err) {
      console.error('[PinnedTemplates] ❌ Reorder failed:', err);
      // Revert optimistic update
      fetchPinnedTemplates();
      alert('Failed to reorder templates');
    }
  };

  if (loading) {
    return (
      <div className="pinned-templates">
        <div className="loading-state">
          <p>Loading favourites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pinned-templates">
        <div className="error-message">
          ❌ {error}
        </div>
      </div>
    );
  }

  return (
    <div className="pinned-templates">
      <div className="favourites-header">
        <h3>Favourites ({pinnedTemplates.length}/{maxPins})</h3>
        <p className="favourites-hint">
          Drag to reorder • Click 🗑️ to remove • Click ⚙️ to execute
        </p>
      </div>

      {pinnedTemplates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📌</div>
          <p className="empty-title">No favourites yet</p>
          <p className="empty-hint">Go to the Browse tab to pin templates</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pinnedTemplates.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="template-list">
              {pinnedTemplates.map((template) => (
                <SortableTemplateItem
                  key={template.id}
                  template={template}
                  onUnpin={handleUnpin}
                  onExecute={onExecute}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default PinnedTemplates;
